import { CenterImage } from "@/components/site/CenterImage";
import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { ClientOnly } from "@tanstack/react-router";
import { ArrowRight, Globe2, MapPin } from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/microsite/Reveal";
import { Magnetic } from "@/components/microsite/Magnetic";
import { CENTERS, centersFromRows, summarizeCountries, countrySlug, type Center } from "@/data/centers";
import { listPublicCenters } from "@/lib/centers-cms.functions";
import { Flag } from "@/components/site/Flag";
import { FaqSection } from "@/components/site/FaqBlocks";
import { BLOCK_DRIVING, faqLang, faqPageLd } from "@/content/faq";
import { getCurrentContentLang, useT } from "@/lib/use-translate";


import { pageSeo, breadcrumbScript } from "@/lib/seo";
const CenterMap = lazy(() => import("@/components/microsite/CenterMap"));

export const Route = createFileRoute("/centeruebersicht")({
  validateSearch: (search: Record<string, unknown>): { country?: string } =>
    typeof search.country === "string" ? { country: search.country } : {},
  loader: async () => {
    // Server-side fetch → centers are in the raw HTML for crawlers.
    try {
      const r = await listPublicCenters();
      return { centers: centersFromRows(r.centers) } as { centers: Center[] };
    } catch {
      return { centers: CENTERS } as { centers: Center[] };
    }
  },
  head: ({ loaderData }) => {
    const centers = loaderData?.centers ?? CENTERS;
    const countries = summarizeCountries(centers).length;
    return {
    ...pageSeo({
      path: '/centeruebersicht',
      title: 'Racing Center & Simulationscenter finden | ACTORACER',
      description: `ACTORACER Racing Center, Simulationscenter und Sim Race Center in ${countries} Ländern. Verifizierte Standorte in Deutschland, Österreich, Schweiz, Frankreich, Spanien, Teneriffa, Portugal, Hongkong.`,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "ACTORACER Sim Race Center Netzwerk",
          numberOfItems: centers.length,
          itemListElement: centers.filter((c) => !c.comingSoon && !c.hideAddress).map((c, i) => {
            const postal = c.postalCity.match(/^(\d{4,5})\s+(.+)$/);
            const address: Record<string, string> = {
              "@type": "PostalAddress",
              streetAddress: c.address,
              addressLocality: postal?.[2] ?? c.postalCity,
              addressCountry: c.countryCode.toUpperCase(),
            };
            if (postal?.[1]) address.postalCode = postal[1];
            const isStockImage = !c.image || c.image.includes("unsplash.com");
            // Centers are independent third-party businesses where ACTORACER
            // simulators can be driven — emitted as Place, NOT LocalBusiness.
            const item: Record<string, unknown> = {
              "@type": "Place",
              name: c.name,
              address,
              geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng },
            };
            if (!isStockImage) item.image = c.image;
            return { "@type": "ListItem", position: i + 1, item };
          }),
        }),
      },
      breadcrumbScript('/centeruebersicht'),
      faqPageLd([BLOCK_DRIVING], faqLang(getCurrentContentLang()), "https://www.actoracer.de/centeruebersicht"),
    ],

    };
  },
  errorComponent: ({ error }) => (
    <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">{(error as Error).message}</div>
  ),
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">Nicht gefunden.</div>
  ),
  component: CenteruebersichtLayout,
});

function CenteruebersichtLayout() {
  const matches = useMatches();
  const isChild = matches.some((m) => m.routeId === "/centeruebersicht/$country");
  if (isChild) return <Outlet />;
  return <CenteruebersichtPage />;
}

function CenteruebersichtPage() {
  const { country: initialCountry } = Route.useSearch();
  const { centers } = Route.useLoaderData() as { centers: Center[] };
  const [country, setCountry] = useState<string>(initialCountry ?? "Alle");
  const [activeSlug, setActiveSlug] = useState<string | undefined>();

  // Counts derive from the data — they can never drift from the list.
  const countrySummary = useMemo(() => summarizeCountries(centers), [centers]);
  const COUNTRY_COUNT = countrySummary.length;
  const CENTER_COUNT = centers.length;

  const filtered = useMemo(
    () => (country === "Alle" ? centers : centers.filter((c) => c.country === country)),
    [country, centers],
  );


  return (
    <>
      <PageHero
        index="09"
        tag="Racing Center & Simulationscenter"
        title="ACTORACER Racing Center"
        accent="& Simulationscenter."
        subtitle={`Vom Hauptsitz in Windeck bis nach Teneriffa und Hongkong — du kannst ACTORACER in ${CENTER_COUNT} verifizierten Racing Centern, Simulationscentern und Sim Race Centern in ${COUNTRY_COUNT} Ländern fahren.`}
      />

      {/* KPI strip + CTA "Eigenes Center eröffnen" */}
      <section className="relative pb-10">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid items-center gap-6 rounded-3xl border border-primary/30 bg-carbon-deep/60 p-6 backdrop-blur-sm md:grid-cols-[1fr_auto] md:p-8">
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <div>
                <div className="hud-label text-primary">Center</div>
                <div className="font-display text-4xl font-black tracking-tight text-white md:text-5xl">{CENTER_COUNT}</div>
              </div>
              <div className="hidden h-12 w-px bg-border/60 md:block" />
              <div>
                <div className="hud-label text-primary">Länder</div>
                <div className="font-display text-4xl font-black tracking-tight text-white md:text-5xl">{COUNTRY_COUNT}</div>
              </div>
              <div className="hidden h-12 w-px bg-border/60 md:block" />
              <div className="flex items-center gap-2 text-sm text-foreground/80">
                <Globe2 className="size-4 text-primary" /> AbA-zertifizierte Motion E-Sports-Center
              </div>
            </div>
            <Magnetic>
              <Link
                to="/eigenes-sim-race-center-eroeffnen"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary-foreground shadow-[0_0_28px_-4px_var(--primary)] transition-transform hover:scale-105"
              >
                Eigenes Center eröffnen <ArrowRight className="size-4" />
              </Link>
            </Magnetic>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="relative pb-12">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="overflow-hidden rounded-2xl border border-primary/30 bg-carbon-deep/60 shadow-[0_0_60px_-20px_var(--primary)]">
            <div className="h-[500px] w-full md:h-[600px]">
              <ClientOnly fallback={<div className="grid h-full place-items-center text-sm text-muted-foreground">Karte wird geladen …</div>}>
                <Suspense fallback={<div className="grid h-full place-items-center text-sm text-muted-foreground">Karte wird geladen …</div>}>
                  <CenterMap centers={filtered} activeSlug={activeSlug} onSelect={setActiveSlug} />
                </Suspense>
              </ClientOnly>
            </div>
          </div>
        </div>
      </section>

      {/* Country filter */}
      <section className="relative pb-10">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="flex flex-wrap gap-2">
            {[{ country: "Alle", flag: "🌍", count: CENTER_COUNT }, ...countrySummary].map((c) => {
              const active = country === c.country;
              const chipCls = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border/60 bg-carbon-deep/60 text-foreground/80 hover:border-primary/60"
              }`;
              const inner = (
                <>
                  <Flag emoji={c.flag} className="inline-block h-4 w-auto align-[-0.15em]" />
                  {c.country}
                  <span className={`rounded-full px-1.5 text-[0.65rem] ${active ? "bg-black/20" : "bg-primary/20 text-primary"}`}>
                    {c.count}
                  </span>
                </>
              );
              if (c.country === "Alle") {
                return (
                  <Link key={c.country} to="/centeruebersicht" className={chipCls} onClick={() => setCountry("Alle")}>
                    {inner}
                  </Link>
                );
              }
              return (
                <Link
                  key={c.country}
                  to="/centeruebersicht/$country"
                  params={{ country: countrySlug(c.country) }}
                  className={chipCls}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Center grid */}
      <section className="relative pb-24 md:pb-32">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c, i) => (
              <Reveal key={c.slug} delay={Math.min(i * 0.03, 0.3)}>
                <CenterCard center={c} onHover={() => setActiveSlug(c.slug)} />
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="py-16 text-center text-sm text-muted-foreground">Noch keine Center in diesem Land gelistet.</p>
          )}

          <div className="mt-14 flex flex-wrap gap-3">
            <Magnetic>
              <Link
                to="/eigenes-sim-race-center-eroeffnen"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary-foreground shadow-[0_0_28px_-4px_var(--primary)] transition-transform hover:scale-105"
              >
                Eigenes Sim Race Center eröffnen <ArrowRight className="size-4" />
              </Link>
            </Magnetic>
          </div>
        </div>
      </section>
      <FaqSection blocks={[BLOCK_DRIVING]} index="09" eyebrow="FAQ" />
    </>
  );
}

function CenterCard({ center: c, onHover }: { center: Center; onHover: () => void }) {
  const t = useT();
  const linkable = Boolean(c.website) && !c.comingSoon;
  const hoverCls = linkable
    ? "transition-all hover:-translate-y-1 hover:border-primary/60 hover:shadow-[0_20px_40px_-20px_var(--primary)]"
    : "";
  const imgHoverCls = linkable ? "transition-transform duration-700 group-hover:scale-110" : "";

  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden">
        <CenterImage src={c.image} alt={c.name} className={`h-full w-full object-cover ${imgHoverCls}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white backdrop-blur">
          <Flag emoji={c.flag} className="inline-block h-4 w-auto align-[-0.15em]" /> {c.country}
        </div>
        {c.comingSoon && (
          <div className="absolute right-4 top-4 inline-flex items-center rounded-full bg-primary px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_18px_-4px_var(--primary)]">
            Standort angekündigt
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 aria-level={2} className="font-display text-lg font-bold leading-tight tracking-tight">{c.name}</h3>
        {!c.hideAddress && (c.address || c.postalCity) && (
          <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
            <MapPin className="mt-0.5 size-3.5 shrink-0 text-primary" />
            <span>
              {c.address}{c.address && c.postalCity && <br />}{c.postalCity}
            </span>
          </div>
        )}
      </div>
    </>
  );

  const baseCls = `group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/50 bg-carbon-deep/55 backdrop-blur-sm ${hoverCls}`;

  if (linkable) {
    return (
      <a
        href={c.website}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={onHover}
        className={baseCls}
        aria-label={t(`${c.name} — Website öffnen`)}
      >
        {inner}
      </a>
    );
  }
  return (
    <div onMouseEnter={onHover} className={baseCls}>
      {inner}
    </div>
  );
}
